import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/multi-tenant-oidc-for-aws-access';
const BLOG_POST_PUBLISHED_AT = '2026-10-09';
const BLOG_POST_READ_TIME = '6 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const BLOG_POST_OG_TITLE = 'Multi-tenant OpenID Connect (OIDC) for SaaS vendors on AWS | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'How we access customer AWS accounts with OIDC, why it beats an ExternalID for multi-tenant SaaS, and why every customer gets their own issuer URL.';

const TRUST_POLICY_SNIPPET = `{
  "Effect": "Allow",
  "Principal": {
    "Federated": "arn:aws:iam::<your-account>:oidc-provider/<issuer-host>/<your-tenant-id>"
  },
  "Action": "sts:AssumeRoleWithWebIdentity",
  "Condition": {
    "StringEquals": {
      "<issuer-host>/<your-tenant-id>:aud": "sts.amazonaws.com",
      "<issuer-host>/<your-tenant-id>:sub": "tenant:<your-tenant-id>"
    }
  }
}`;

const SHARED_SNIPPET = `# one issuer for every customer: the tenant sits in a claim
issuer: https://<issuer-host>
sub:    tenant:<your-tenant-id>      # your trust policy must check this

# one issuer per customer: the tenant sits in the issuer
issuer: https://<issuer-host>/<your-tenant-id>`;

const BlogOidcAwsAccessPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T12:00:00.000Z`}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post blog-post--centered">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              Multi-tenant OpenID Connect (OIDC) for SaaS vendors on AWS
            </h1>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Engineering</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>9 Oct 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__content">
            <p>
              When building Circumvent, we knew we wanted to ingest findings, resources and other metadata from our
              customers&apos; AWS accounts, and we wanted to do it as securely as we could in a multi-tenant
              environment. A lot of SaaS products have the same need, so this post describes how we went about it.
            </p>
            <p>
              Any SaaS product that needs this access has to pick how to authenticate to each customer&apos;s AWS account. AWS gives you
              four main ways to do it:
            </p>
            <ul>
              <li>
                <strong>Static access keys:</strong> The customer makes an IAM user and sends you the key pair. The key
                never expires, and you are now on the hook for rotating and storing it securely.
              </li>
              <li>
                <strong>Cross-account role with an ExternalID:</strong> The customer&apos;s role trusts your AWS
                account, and you assume it. No long-lived secret changes hands, but on its own a customer could trick you into
                using their role on someone else&apos;s behalf, which AWS calls the{' '}
                <a
                  href="https://docs.aws.amazon.com/IAM/latest/UserGuide/confused-deputy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  confused deputy problem
                </a>
                . To stop
                that, the role also asks for an ExternalID, a unique ID that you send each time you assume it. This is
                the most common setup today.
              </li>
              <li>
                <strong>IAM Roles Anywhere:</strong> You sign in with an X.509 certificate that the customer&apos;s
                account trusts. It works from outside AWS, but you hold a private key and must issue and renew
                certificates.
              </li>
              <li>
                <strong>OpenID Connect (OIDC):</strong> You sign a short-lived token and AWS swaps it for temporary
                credentials. You store no long-lived secret for the customer and AWS stores none for you.
              </li>
            </ul>
            <p>
              We don&apos;t use static access keys or IAM Roles Anywhere. Both require you to store a long-lived secret for each
              customer. This
              post covers why OIDC beats an ExternalID when you serve many customers, and why we give each customer
              their own issuer URL.
            </p>

            <h2>How OIDC works</h2>
            <p>
              You sign a short-lived token that says who you are and which customer the request is for. AWS checks the
              signature against your public keys, checks the token against the rules in the customer&apos;s role, and
              returns short-lived credentials. AWS calls this{' '}
              <a
                href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_providers_oidc.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                web identity federation
              </a>
              .
            </p>

            <figure className="blog-diagram">
              <img
                src="/images/blog/arch-oidc-federation.svg"
                alt="Diagram: a vendor backend service asks a signing service for a token, the signing service signs it with a KMS key, the backend service sends it to AWS STS, STS fetches the vendor's public keys and checks the customer role's trust policy, then returns short-lived credentials."
                loading="lazy"
                decoding="async"
              />
            </figure>

            <h2>Why not a classic cross-account role with an ExternalID?</h2>
            <p>
              The customer&apos;s role trusts one of your AWS principals and requires an
              ExternalID. Your code assumes the role and sends that ID. The ID is not a secret, but it must be unique per customer, and you must generate it. If customers pick their own, one could trick your system into using another customer&apos;s ID. A proper implementation does its job but leaves some gaps:
            </p>
            <ul>
              <li>
                <strong>The ExternalID lookup is the security boundary.</strong> Your code
                picks which ExternalID to send for each customer. If it picks the wrong one (ex: does lookup based on AWS account ID instead of customer ID), AWS lets the request through.
              </li>
              <li>
                <strong>Customers hardcode your AWS identity.</strong> Each customer&apos;s trust policy names your AWS
                account or role. If you move accounts, split environments or recreate the role, every customer must
                update theirs.
              </li>
              <li>
                <strong>The ExternalID protection is optional on the customer&apos;s side.</strong> If a customer
                leaves the ExternalID condition out of their role, it still works and nothing warns them. Your template
                can include it, but the customer can edit it out.
                <ul>
                  <li>
                    A common fix: when a customer gives you a role ARN, first try to assume it without the ExternalID.
                    If that works, don&apos;t save the role until the customer adds the condition.{' '}
                    <a
                      href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_common-scenarios_third-party.html#external-id-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AWS recommends this check
                    </a>{' '}
                    in its docs.
                  </li>
                </ul>
              </li>
            </ul>
            <p>OIDC helps with these:</p>
            <ul>
              <li>
                A separate signing service decides which customer a token is for and signs that in. Your backend services
                can&apos;t make a token for a customer the signer did not approve.
              </li>
              <li>
                Customers trust your issuer (for example, <code>https://oidc.example.com</code>) instead of your AWS
                account, so you can change your AWS setup (for example, disaster recovery or account migration) without asking them to update anything.
              </li>
              <li>
                To forge a token, an attacker has to reach the signer. You can
                lock the signer down and log every token it signs.
              </li>
            </ul>

            <h2>One issuer for all customers, or one issuer per customer?</h2>
            <p>
              You have two ways to say which customer a token is for. You can put the customer identifier in a claim, or you can
              put the customer identifier in the issuer URL.
            </p>

            <pre className="blog-code">
              <code>{SHARED_SNIPPET}</code>
            </pre>

            <p>
              AWS calls these{' '}
              <a
                href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_providers_oidc_secure-by-default.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                shared and private providers
              </a>
              . With a shared issuer, the customer&apos;s role must check the correct claim. If it does not, a token for
              any customer of yours opens that role (similar to the confused deputy problem with an ExternalID).
            </p>
            <p>
              AWS treats this as a real risk. For well-known shared issuers such as GitHub Actions, GitLab and Terraform
              Cloud, IAM refuses to save a trust policy that omits the required claim. The check only covers issuers
              on AWS&apos;s list, so a new vendor&apos;s issuer is not covered.
            </p>
            <p>
              A per-customer issuer makes this much harder to get wrong. Each customer registers an IAM OIDC provider that points at
              their own issuer URL. AWS only accepts tokens whose issuer matches a provider in that account. A token
              from customer B&apos;s issuer cannot match customer A&apos;s provider, even if A&apos;s trust policy is
              missing the claim condition.
            </p>

            <h2>What the trust policy looks like</h2>
            <pre className="blog-code">
              <code>{TRUST_POLICY_SNIPPET}</code>
            </pre>
            <p>
              The role trusts one provider: your issuer for this customer. The two conditions are optional, but each
              adds a check:
            </p>
            <ul>
              <li>
                <code>aud</code> says who the token is for. If you use the same issuer for Azure or GCP, a token made
                for Azure will not work on AWS.
              </li>
              <li>
                <code>sub</code> says who the token is about. If your signer ever puts the wrong tenant ID in a token,
                this role still rejects it, as a second layer of defense.
              </li>
            </ul>

            <h2>The same issuer works in Azure and GCP</h2>
            <p>
              OIDC is an open standard, so it works outside AWS too. Azure and GCP can also trust a token from your issuer.
              In Azure, you add a federated credential to an Entra ID application. In GCP, you add a workload identity pool provider.
              A separate issuer URL for each customer works the same way there, so one signing service can serve a customer on all three clouds.
            </p>

            <h2>What it costs you</h2>
            <ul>
              <li>
                You run a public service. AWS fetches your public keys from your issuer URL. If it is down or serves
                bad keys, no customer can get credentials.
              </li>
              <li>
                You run a signing service. It can mint a token for any customer, so protect it and turn on audit
                logging.
              </li>
            </ul>

            <h2>How we do it</h2>
            <p>
              Each customer gets their own issuer URL. When you connect an AWS account, our CloudFormation template
              creates a provider for that URL and a read-only role that trusts it. We hold no key for your account. When
              we need to read it, our signing service issues a token for your tenant and we swap it for short-lived
              credentials that expire on their own. The signing key lives in AWS KMS so our code never has to manage it directly.
            </p>

            <div className="blog-post__cta">
              <h3>Cut the noise in your cloud findings</h3>
              <p>
                Circumvent checks each cloud and application security finding, shows the evidence, and sends only verified work to
                the right owner. Talk to us about it.
              </p>
              <Link to="/#contact" className="btn btn-solid">
                Book a demo
              </Link>
            </div>

            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogOidcAwsAccessPage;
